import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, MapPin, Camera, Calendar, ChevronRight, Image as ImageIcon, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Journal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEntry, setEditEntry] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [editPhotos, setEditPhotos] = useState([]);
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchJournal();
  }, [user, navigate]);

  const fetchJournal = async () => {
    try {
      const res = await axios.get(`${API}/api/users/journal`, { withCredentials: true });
      setEntries(res.data.entries || []);
    } catch (err) {
      console.error('Failed to load journal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpen = (entry) => {
    setEditEntry(entry);
    setEditNote(entry.note || '');
    setEditPhotos(entry.photos || []);
    setEditDate(entry.visited_date || '');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setEditPhotos(prev => [...prev, response.data.url]);
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!editEntry) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/activities/${editEntry.activity_id}/been-there/journal`, {
        photos: editPhotos,
        note: editNote,
        visited_date: editDate
      }, { withCredentials: true });
      setEditEntry(null);
      fetchJournal();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      <header className="sticky top-0 z-40 glass border-b border-[#2C302E]/5">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/10 flex items-center justify-center" data-testid="journal-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-[#2C302E] font-['Outfit']">{t('journal_title')}</h1>
            <p className="text-sm text-[#666B67]">{entries.length} {t('journal_adventures')}</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-[#81B29A]/10 flex items-center justify-center mx-auto mb-4">
              <Camera className="w-8 h-8 text-[#81B29A]" />
            </div>
            <h2 className="text-xl font-semibold text-[#2C302E] mb-2">{t('journal_empty_title')}</h2>
            <p className="text-[#666B67] mb-6 max-w-xs mx-auto">{t('journal_empty_desc')}</p>
            <Button onClick={() => navigate('/discover')} className="btn-primary">{t('saved_discover_btn')}</Button>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#2C302E]/10" />

            {entries.map((entry, idx) => (
              <div key={idx} className="relative flex gap-4 mb-6 pl-10" data-testid={`journal-entry-${idx}`}>
                {/* Timeline dot */}
                <div className="absolute left-2.5 top-2 w-3 h-3 rounded-full bg-[#E07A5F] border-2 border-white" />
                
                <div className="flex-1 bg-white rounded-2xl overflow-hidden card-hover">
                  {/* Activity image */}
                  {entry.activity_image && (
                    <div className="relative h-36 cursor-pointer" onClick={() => navigate(`/activity/${entry.activity_id}`)}>
                      <img src={entry.activity_image} alt={entry.activity_title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-3 left-3">
                        <h3 className="font-semibold text-white text-sm">{entry.activity_title}</h3>
                        <p className="text-xs text-white/80 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{entry.activity_city}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="p-4">
                    {/* Date */}
                    <div className="flex items-center gap-1 text-xs text-[#666B67] mb-2">
                      <Calendar className="w-3 h-3" />
                      {new Date(entry.visited_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>

                    {/* Note */}
                    {entry.note && (
                      <p className="text-sm text-[#2C302E] mb-3">{entry.note}</p>
                    )}

                    {/* Journal Photos */}
                    {entry.photos?.length > 0 && (
                      <div className="flex gap-2 mb-3 overflow-x-auto hide-scrollbar">
                        {entry.photos.map((photo, pi) => (
                          <img key={pi} src={photo} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                        ))}
                      </div>
                    )}

                    {/* Edit button */}
                    <button
                      onClick={() => handleEditOpen(entry)}
                      className="text-xs text-[#E07A5F] font-medium flex items-center gap-1"
                      data-testid={`edit-journal-${idx}`}
                    >
                      <Camera className="w-3 h-3" />
                      {entry.photos?.length > 0 || entry.note ? t('journal_edit') : t('journal_add_memories')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Edit Journal Dialog */}
      <Dialog open={!!editEntry} onOpenChange={() => setEditEntry(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('journal_edit_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#2C302E] mb-1 block">{t('journal_date')}</label>
              <input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="w-full px-3 py-2 border border-[#2C302E]/10 rounded-xl text-sm"
                data-testid="journal-date-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C302E] mb-1 block">{t('journal_note')}</label>
              <Textarea
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder={t('journal_note_placeholder')}
                className="min-h-[80px]"
                data-testid="journal-note-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2C302E] mb-1 block">{t('journal_photos')}</label>
              {editPhotos.length > 0 && (
                <div className="flex gap-2 mb-2 flex-wrap">
                  {editPhotos.map((photo, i) => (
                    <div key={i} className="relative">
                      <img src={photo} alt="" className="w-16 h-16 rounded-lg object-cover" />
                      <button onClick={() => setEditPhotos(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-[#F9F8F6] rounded-xl text-sm text-[#666B67] hover:bg-[#E07A5F]/10 hover:text-[#E07A5F] transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                {t('journal_add_photo')}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploading} data-testid="journal-photo-upload" />
              </label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditEntry(null)} className="flex-1">{t('common_cancel')}</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 btn-primary" data-testid="journal-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('journal_save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default Journal;
